import { ReactElement, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import Box from '@mui/material/Box'
import { Input, Modal, Select } from 'components'
import { TInstrumentType, TInstrumentTypeWithId } from 'types/models'
import { useInstrumentTypesService } from 'services/instrumentsService'
import { ModalContainer, ModalForm } from '../../styles'

interface Props {
  onConfirm?(data: TInstrumentTypeWithId | TInstrumentType): Promise<void>
}

const PARSER_LABELS: Record<string, string> = {
  dx_parser_class: 'DX Parser',
  json_parser_class: 'JSON Parser',
  csv_parser_class: 'CSV Parser',
}

const useAddEditDeviceTypeModal = ({ onConfirm }: Props = {}): [
  ReactElement,
  (deviceType?: TInstrumentTypeWithId) => void,
] => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deviceType, setDeviceType] = useState<TInstrumentTypeWithId | null>(
    null
  )

  const { getInstrumentTypeParsers } = useInstrumentTypesService()

  const { data: parsersData } = useQuery({
    queryKey: ['instrument_type_parsers'],
    queryFn: getInstrumentTypeParsers,
    staleTime: 5 * 60 * 1000,
  })

  const parsers = parsersData?.result

  const toggleModal = () => setIsOpen(!isOpen)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TInstrumentType>({
    defaultValues: {
      name: '',
      manufacturer: '',
      first_x: null,
      last_x: null,
      dx_parser_class: null,
      json_parser_class: null,
      csv_parser_class: null,
    },
  })

  useEffect(() => {
    if (deviceType) {
      reset({
        name: deviceType.name,
        manufacturer: deviceType.manufacturer,
        first_x: deviceType.first_x,
        last_x: deviceType.last_x,
        dx_parser_class: deviceType.dx_parser_class ?? null,
        json_parser_class: deviceType.json_parser_class ?? null,
        csv_parser_class: deviceType.csv_parser_class ?? null,
      })
    } else {
      reset({
        name: '',
        manufacturer: '',
        first_x: null,
        last_x: null,
        dx_parser_class: null,
        json_parser_class: null,
        csv_parser_class: null,
      })
    }
  }, [deviceType, reset])

  const onSubmit = async (data: TInstrumentType) => {
    const payload = deviceType?.id
      ? ({ ...data, id: deviceType.id } as TInstrumentTypeWithId)
      : data

    setIsSubmitting(true)
    try {
      await onConfirm?.(payload)
      toggleModal()
      reset()
    } catch {
      // Modal stays open, error handled by mutation
    } finally {
      setIsSubmitting(false)
    }
  }

  const onClose = () => {
    if (isSubmitting) return
    toggleModal()
    reset()
  }

  const availableParserKeys = parsers
    ? (Object.keys(parsers) as Array<keyof typeof parsers>).filter(
        (key) => parsers[key].length > 0
      )
    : []

  return [
    <Modal
      isOpen={isOpen}
      title={deviceType ? 'Edit Device Type' : 'Add Device Type'}
      okButtonType="submit"
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
    >
      <ModalForm onSubmit={handleSubmit(onSubmit)} id="hook-form">
        <ModalContainer>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field }) => (
              <Input
                {...field}
                label="Name"
                errorMsg={errors.name?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="manufacturer"
            control={control}
            rules={{ required: 'Manufacturer is required' }}
            render={({ field }) => (
              <Input
                {...field}
                label="Manufacturer"
                errorMsg={errors.manufacturer?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="first_x"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Input
                  {...field}
                  value={value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    if (Number(val) < 0) return

                    onChange(val === '' ? null : Number(val))
                  }}
                  label="First X"
                  type="number"
                  errorMsg={errors.first_x?.message}
                  fullWidth
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="last_x"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Input
                  {...field}
                  value={value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    if (Number(val) < 0) return
                    onChange(val === '' ? null : Number(val))
                  }}
                  label="Last X"
                  type="number"
                  errorMsg={errors.last_x?.message}
                  fullWidth
                  disabled={isSubmitting}
                />
              )}
            />
          </Box>

          {availableParserKeys.map((parserKey) => (
            <Controller
              key={parserKey}
              name={parserKey}
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Select
                  {...field}
                  value={value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    onChange(val === '' ? null : val)
                  }}
                  label={PARSER_LABELS[parserKey] ?? parserKey}
                  fullWidth
                  disabled={isSubmitting}
                  options={[
                    { value: '', label: 'None' },
                    ...parsers![parserKey].map((option) => ({
                      value: option,
                      label: option,
                    })),
                  ]}
                />
              )}
            />
          ))}
        </ModalContainer>
      </ModalForm>
    </Modal>,
    (data?: TInstrumentTypeWithId) => {
      setDeviceType(data || null)
      toggleModal()
    },
  ]
}

export default useAddEditDeviceTypeModal
